/* global theme, Shopify */

if (!customElements.get('cart-rebate')) {
  class CartRebate extends HTMLElement {
    static listenersAttached = false;
    static instantSavingCents = 0;
    static lastCartTotalCents = 0;
    static syncedInstantSavingCents = null;
    static attributeSyncTimer = null;
    static PRODUCTION_API_URL = 'https://parkave-rebate-engine.vercel.app/api/rebate';
    static LOCAL_API_URL = 'http://localhost:3000/api/rebate';

    /**
     * Toggle storefront instant savings (cart total adjustment + instant rebate UI).
     * Set to true when re-enabling instant rebates.
     * @returns {boolean}
     */
    static isInstantRebatesEnabled() {
      return false;
    }

    /**
     * Whether the theme is running in a local development context.
     * @returns {boolean}
     */
    static isLocalDevelopment() {
      const { hostname } = window.location;
      if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
      return Shopify?.theme?.role === 'development';
    }

    /**
     * Rebate API URL — production engine (local override via LOCAL_API_URL when needed).
     * @returns {string}
     */
    static getApiUrl() {
      return CartRebate.PRODUCTION_API_URL;
    }

    constructor() {
      super();
      this.apiUrl = CartRebate.getApiUrl();
      this.abortController = null;
      this.lastCart = null;
    }

    connectedCallback() {
      CartRebate.attachDocumentListeners();
      this.fetchRebates();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    /**
     * Registers document-level cart event listeners once so they survive
     * cart drawer section re-renders.
     */
    static attachDocumentListeners() {
      if (CartRebate.listenersAttached) return;
      CartRebate.listenersAttached = true;

      CartRebate.handleCartEvent = (event) => {
        const cart = event.detail?.cart;
        if (cart) {
          const saving = CartRebate.isInstantRebatesEnabled() ? CartRebate.instantSavingCents : 0;
          CartRebate.applyInstantSavings(saving, cart.total_price);
        }

        const rebateEls = document.querySelectorAll('cart-rebate');
        rebateEls.forEach((rebateEl) => rebateEl.fetchRebates(cart));
      };

      document.addEventListener('on:cart:add', CartRebate.handleCartEvent);
      document.addEventListener('on:line-item:change', CartRebate.handleCartEvent);
      document.addEventListener('on:cart-drawer:after-open', CartRebate.handleCartEvent);
    }

    /**
     * Formats cents using the shop money-with-currency format.
     * @param {number} cents - Amount in cents.
     * @returns {string}
     */
    static formatMoneyFromCents(cents, withCurrency = true, noDecimals = false) {
      let format = withCurrency
        ? (theme.settings.moneyWithCurrencyFormat || '${{amount}}')
        : (theme.settings.moneyFormat || '${{amount}}');

      if (noDecimals) {
        format = format.replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}');
      }
      const dollars = cents / 100;
      const amount = dollars.toFixed(2);
      const amountNoDecimals = Math.round(dollars).toString();
      const amountWithComma = amount.replace('.', ',');

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountWithComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    /**
     * Subtracts instant savings from every marked cart total on the page.
     * Persists the amount on the cart for checkout integrations (Shopify Function).
     * @param {number} instantSavingCents - Instant savings in cents.
     * @param {number} [cartTotalCents] - Shopify cart total in cents.
     */
    static applyInstantSavings(instantSavingCents, cartTotalCents) {
      const saving = Math.max(0, Math.round(instantSavingCents));
      CartRebate.instantSavingCents = saving;
      if (typeof cartTotalCents === 'number') {
        CartRebate.lastCartTotalCents = cartTotalCents;
      }

      document.querySelectorAll('[data-instant-savings-total]').forEach((el) => {
        const baseTotal = Number(el.dataset.cartTotalCents);
        if (Number.isNaN(baseTotal)) return;

        const adjusted = Math.max(0, baseTotal - saving);
        el.textContent = CartRebate.formatMoneyFromCents(adjusted);
        el.classList.toggle('cart-total--adjusted', saving > 0);
        el.dataset.adjustedTotalCents = String(adjusted);
      });

      document.dispatchEvent(new CustomEvent('on:instant-savings:update', {
        bubbles: true,
        detail: {
          instantSavingCents: saving,
          cartTotalCents: CartRebate.lastCartTotalCents,
          adjustedTotalCents: Math.max(0, CartRebate.lastCartTotalCents - saving)
        }
      }));

      CartRebate.syncCartAttribute(saving);
    }

    /**
     * Stores instant savings on the cart so checkout can apply the same discount.
     * @param {number} instantSavingCents - Instant savings in cents.
     */
    static syncCartAttribute(instantSavingCents) {
      if (instantSavingCents === CartRebate.syncedInstantSavingCents) return;

      clearTimeout(CartRebate.attributeSyncTimer);
      CartRebate.attributeSyncTimer = setTimeout(async () => {
        try {
          const response = await fetch(theme.routes.cartUpdate, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              attributes: {
                _instant_saving_cents: instantSavingCents > 0 ? String(instantSavingCents) : ''
              }
            })
          });
          if (!response.ok) throw new Error(response.status);
          CartRebate.syncedInstantSavingCents = instantSavingCents;
        } catch (error) {
          console.warn('[cart-rebate] Could not sync instant savings to cart attributes', error);
        }
      }, 300);
    }

    /**
     * Returns today's date in YYYY-MM-DD format (local timezone).
     * @returns {string}
     */
    getPurchaseDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    /**
     * Extracts SKU list from cart items, repeating each SKU by line quantity.
     * @param {object} cart - Cart object from /cart.js.
     * @returns {string[]}
     */
    getSkusFromCart(cart) {
      const skus = [];
      cart.items.forEach((item) => {
        if (!item.sku) return;
        for (let i = 0; i < item.quantity; i += 1) {
          skus.push(item.sku);
        }
      });
      return skus;
    }

    /**
     * Formats a dollar amount for display.
     * @param {number} amount - Dollar amount.
     * @returns {string}
     */
    formatMoney(amount, withCurrency = true, noDecimals = false) {
      return CartRebate.formatMoneyFromCents(Math.round(amount * 100), withCurrency, noDecimals);
    }

    /**
     * Escapes HTML special characters.
     * @param {string} str - String to escape.
     * @returns {string}
     */
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /**
     * Whether a rebate line item counts as instant savings.
     * @param {object} rebate - Rebate line item from API.
     * @returns {boolean}
     */
    isInstantRebate(rebate) {
      if (!CartRebate.isInstantRebatesEnabled()) return false;

      const type = rebate.type || rebate.rebateType;
      if (type === 'instant_saving' || type === 'instant_discount') return true;
      if (type === 'bonus_rebate' || type === 'mail_in') return false;

      const name = (rebate.program_name || rebate.programName || '').toLowerCase();
      return name.includes('instant');
    }

    /**
     * @param {object} rebate - Rebate line item from API.
     * @returns {string}
     */
    getProgramName(rebate) {
      return rebate.program_name || rebate.programName || '';
    }

    /**
     * @param {object} rebate - Rebate line item from API.
     * @returns {string}
     */
    getFormUrl(rebate) {
      return rebate.form_url || rebate.formUrl || '';
    }

    /**
     * Normalizes API totals from top-level fields or the rebates array.
     * @param {object} data - Rebate API response.
     * @returns {{ instantTotal: number, mailInTotal: number, rebates: object[], availableRebates: object[] }}
     */
    normalizeRebateData(data) {
      const rebates = data.rebates || [];
      const availableRebates = data.available_rebates || data.availableRebates || [];
      let instantTotal = CartRebate.isInstantRebatesEnabled()
        ? Number(data.instant_saving ?? data.instantDiscountTotal) || 0
        : 0;
      let mailInTotal = Number(data.bonus_rebates ?? data.mailInTotal) || 0;

      if (!instantTotal && !mailInTotal && rebates.length > 0) {
        if (CartRebate.isInstantRebatesEnabled()) {
          instantTotal = rebates
            .filter((rebate) => this.isInstantRebate(rebate))
            .reduce((sum, rebate) => sum + (Number(rebate.amount) || 0), 0);
        }

        mailInTotal = rebates
          .filter((rebate) => !this.isInstantRebate(rebate))
          .reduce((sum, rebate) => sum + (Number(rebate.amount) || 0), 0);
      }

      return { instantTotal, mailInTotal, rebates, availableRebates };
    }

    /**
     * @param {object} rebate - Rebate line item from API.
     * @returns {number}
     */
    getRebateAmount(rebate) {
      return Number(rebate.amount) || 0;
    }

    /**
     * Builds HTML for a single rebate form link row.
     * Available-rebate items have no dollar amount — omit the amount label.
     * @param {object} rebate - Rebate or available-rebate line item.
     * @param {boolean} showAmount - Whether to show the money-back amount.
     * @returns {string}
     */
    renderLinkItem(rebate, showAmount) {
      const amount = this.getRebateAmount(rebate);
      const amountHtml = showAmount && amount > 0
        ? `<span class="cart-rebate__link-amount">${this.escapeHtml(this.formatMoney(amount, false, true))} back</span>`
        : '';

      return `
        <li>
          <a class="cart-rebate__link" href="${this.escapeHtml(this.getFormUrl(rebate))}" target="_blank" rel="noopener noreferrer">
            <span class="cart-rebate__link-text">
              <span class="cart-rebate__link-title">${this.escapeHtml(this.getProgramName(rebate))}</span>
              ${amountHtml}
            </span>
            <svg class="cart-rebate__link-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06z"/></svg>
          </a>
        </li>`;
    }

    /**
     * Builds HTML for rebate form links (dollar rebates + available promotions).
     * @param {object[]} rebates - Rebate line items with form URLs and amounts.
     * @param {object[]} availableRebates - Available rebate/promo items without amounts.
     * @returns {string}
     */
    renderLinksSection(rebates, availableRebates = []) {
      const rebateLinks = rebates.filter((rebate) => this.getFormUrl(rebate));
      const availableLinks = availableRebates.filter((rebate) => this.getFormUrl(rebate));
      const totalCount = rebateLinks.length + availableLinks.length;
      if (totalCount === 0) return '';

      return `
        <h3 class="cart-rebate__section-title">Rebate Forms (${totalCount})</h3>
        <ul class="cart-rebate__links" role="list">
          ${rebateLinks.map((rebate) => this.renderLinkItem(rebate, true)).join('')}
          ${availableLinks.map((rebate) => this.renderLinkItem(rebate, false)).join('')}
        </ul>
      `;
    }

    /**
     * Renders rebate data into the element.
     * @param {object} data - Rebate API response.
     * @param {object} cart - Cart object from /cart.js.
     */
    render(data, cart) {
      const { instantTotal, mailInTotal, rebates, availableRebates } = this.normalizeRebateData(data);
      const totalRebate = instantTotal + mailInTotal;
      const hasAvailableOnly = totalRebate <= 0 && availableRebates.some((rebate) => this.getFormUrl(rebate));
      const instantSavingCents = CartRebate.isInstantRebatesEnabled()
        ? Math.round(instantTotal * 100)
        : 0;

      CartRebate.applyInstantSavings(instantSavingCents, cart.total_price);

      if (totalRebate <= 0 && !hasAvailableOnly) {
        this.hide();
        return;
      }

      const linksSectionHtml = this.renderLinksSection(rebates, availableRebates);

      if (hasAvailableOnly) {
        this.innerHTML = `
          <div class="cart-rebate__card">
            <div class="cart-rebate__header">
              <div class="cart-rebate__badge">Rebates/Promotions Available</div>
            </div>
            <div class="cart-rebate__body">
              ${linksSectionHtml}
              <p class="cart-rebate__disclaimer">
                View Rebate Forms for final details.
              </p>
            </div>
          </div>
        `;
      } else {
        this.innerHTML = `
          <div class="cart-rebate__card">
            <div class="cart-rebate__header">
              <div class="cart-rebate__badge">Rebates Unlocked</div>
              <p class="cart-rebate__headline">
                You're getting <span class="cart-rebate__amount">${this.escapeHtml(this.formatMoney(totalRebate, false, true))}</span> back in rebates
              </p>
            </div>
            <div class="cart-rebate__body">
              ${linksSectionHtml}
              <p class="cart-rebate__disclaimer">
                Estimated total. Submit each form online to claim — final amounts are confirmed on the official rebate forms.
              </p>
            </div>
          </div>
        `;
      }

      this.hidden = false;
      this.dispatchHeightUpdate();
    }

    /**
     * Hides the rebate section and clears its content.
     */
    hide() {
      this.hidden = true;
      this.innerHTML = '';
      this.dispatchHeightUpdate();
    }

    /**
     * Clears rebate UI and resets adjusted totals.
     * @param {object} [cart] - Cart object from /cart.js.
     */
    clearRebates(cart) {
      this.hide();
      CartRebate.applyInstantSavings(0, cart?.total_price ?? 0);
    }

    /**
     * Notifies the cart drawer to recalculate sticky summary heights.
     */
    dispatchHeightUpdate() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Fetches cart SKUs and loads rebate data from the API.
     * @param {object} [cart] - Optional cart object from a cart event response.
     */
    async fetchRebates(cart) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        if (!cart) {
          const cartResponse = await fetch(`${theme.routes.cart}.js`, {
            signal: this.abortController.signal
          });
          if (!cartResponse.ok) throw new Error(cartResponse.status);
          cart = await cartResponse.json();
        }

        this.lastCart = cart;

        if (!cart.items || cart.items.length === 0) {
          this.clearRebates(cart);
          return;
        }

        const skus = this.getSkusFromCart(cart);
        if (skus.length === 0) {
          this.clearRebates(cart);
          return;
        }

        const rebateResponse = await fetch(this.apiUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            skus,
            purchaseDate: this.getPurchaseDate()
          }),
          signal: this.abortController.signal
        });

        if (!rebateResponse.ok) throw new Error(rebateResponse.status);

        const data = await rebateResponse.json();
        this.render(data, cart);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.clearRebates(this.lastCart);
      }
    }
  }

  customElements.define('cart-rebate', CartRebate);
}
